/**
 * @fileoverview Firestore-based contact form submission system.
 * 
 * This service handles the storage and management of contact form submissions
 * for user inquiries, bug reports, feature requests, and other feedback.
 */

import { db } from '../firebase/config';
import { 
  addDoc, 
  collection, 
  Timestamp, 
  query, 
  where, 
  orderBy, 
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';

// Contact form status options
export type ContactStatus = 'New' | 'In Progress' | 'Responded' | 'Closed' | 'Spam';

// Contact form data structure
export interface ContactFormData {
  id?: string; // Firestore document ID
  timestamp: string;
  name: string;
  email: string;
  contactType: string;
  subject: string;
  message: string;
  browserInfo?: string;
  appVersion?: string;
  status: ContactStatus;
  adminNotes?: string;
  adminResponse?: string;
  respondedAt?: string;
  respondedBy?: string;
  contactId: string; // Unique identifier for the contact
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

class ContactFormService {
  private readonly COLLECTION_NAME = 'contactSubmissions';

  /**
   * Generate a unique contact ID
   */
  private generateContactId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `CONT-${timestamp}-${random}`;
  }

  /**
   * Submit a contact form to Firestore
   */
  async submitContactForm(contactData: Omit<ContactFormData, 'timestamp' | 'status' | 'contactId' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; contactId?: string; error?: string }> {
    try {
      // Generate unique contact ID
      const contactId = this.generateContactId();
      
      // Add timestamp and initial status
      const fullContactData: Omit<ContactFormData, 'id'> = {
        ...contactData,
        timestamp: new Date().toISOString(),
        status: 'New', // Initial status
        contactId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), fullContactData);
      
      console.log('✅ Contact form submitted successfully with ID:', contactId, 'Document ID:', docRef.id);
      return { success: true, contactId };
    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get all contact submissions with optional filtering
   */
  async getContactSubmissions(filters?: {
    status?: ContactStatus;
    contactType?: string;
    limit?: number;
  }): Promise<{ submissions: ContactFormData[]; error?: string }> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME), orderBy('createdAt', 'desc'));
      
      // Apply filters
      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters?.contactType) {
        q = query(q, where('contactType', '==', filters.contactType));
      }

      const querySnapshot = await getDocs(q);
      const submissions: ContactFormData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as ContactFormData;
        submissions.push({
          ...data,
          id: doc.id
        });
      });

      // Apply limit if specified
      const limitedSubmissions = filters?.limit ? submissions.slice(0, filters.limit) : submissions;
      
      return { submissions: limitedSubmissions };
    } catch (error) {
      console.error('❌ Error fetching contact submissions:', error);
      return { submissions: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get a single contact submission by ID
   */
  async getContactSubmissionById(contactId: string): Promise<{ submission?: ContactFormData; error?: string }> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME), 
        where('contactId', '==', contactId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { error: 'Contact submission not found' };
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data() as ContactFormData;
      
      return { submission: { ...data, id: doc.id } };
    } catch (error) {
      console.error('❌ Error fetching contact submission:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Update contact submission status and admin notes
   */
  async updateContactStatus(
    contactId: string, 
    newStatus: ContactStatus, 
    adminNotes?: string,
    adminResponse?: string,
    respondedBy?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First find the document by contactId
      const q = query(
        collection(db, this.COLLECTION_NAME), 
        where('contactId', '==', contactId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Contact submission not found' };
      }

      const docRef = querySnapshot.docs[0].ref;
      const updateData: Partial<ContactFormData> = {
        status: newStatus,
        updatedAt: Timestamp.now()
      };

      if (adminNotes !== undefined) {
        updateData.adminNotes = adminNotes;
      }

      if (adminResponse !== undefined) {
        updateData.adminResponse = adminResponse;
      }

      if (respondedBy) {
        updateData.respondedBy = respondedBy;
      }

      if (newStatus === 'Responded' || newStatus === 'Closed') {
        updateData.respondedAt = new Date().toISOString();
      }

      await updateDoc(docRef, updateData);
      
      console.log(`✅ Contact status updated to "${newStatus}" for contact ID: ${contactId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating contact status:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Delete a contact submission
   */
  async deleteContactSubmission(contactId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Find the document by contactId
      const q = query(collection(db, this.COLLECTION_NAME), where('contactId', '==', contactId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Contact submission not found' };
      }
      
      // Delete the document
      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
      
      console.log('✅ Contact submission deleted successfully:', contactId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting contact submission:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get contact submission statistics
   */
  async getContactStats(): Promise<{
    total: number;
    new: number;
    inProgress: number;
    responded: number;
    closed: number;
    spam: number;
    byContactType: Record<string, number>;
    error?: string;
  }> {
    try {
      const { submissions } = await this.getContactSubmissions();
      
      const stats = {
        total: submissions.length,
        new: 0,
        inProgress: 0,
        responded: 0,
        closed: 0,
        spam: 0,
        byContactType: {} as Record<string, number>
      };

      submissions.forEach(submission => {
        // Count by status
        switch (submission.status) {
          case 'New':
            stats.new++;
            break;
          case 'In Progress':
            stats.inProgress++;
            break;
          case 'Responded':
            stats.responded++;
            break;
          case 'Closed':
            stats.closed++;
            break;
          case 'Spam':
            stats.spam++;
            break;
        }

        // Count by contact type
        const contactType = submission.contactType;
        stats.byContactType[contactType] = (stats.byContactType[contactType] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('❌ Error getting contact stats:', error);
      return {
        total: 0,
        new: 0,
        inProgress: 0,
        responded: 0,
        closed: 0,
        spam: 0,
        byContactType: {},
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get available status options
   */
  getStatusOptions(): ContactStatus[] {
    return ['New', 'In Progress', 'Responded', 'Closed', 'Spam'];
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: ContactStatus): string {
    switch (status) {
      case 'New':
        return '#ef4444'; // red
      case 'In Progress':
        return '#f59e0b'; // amber
      case 'Responded':
        return '#3b82f6'; // blue
      case 'Closed':
        return '#10b981'; // green
      case 'Spam':
        return '#6b7280'; // gray
      default:
        return '#6b7280'; // gray
    }
  }
}

// Export singleton instance
export const contactFormService = new ContactFormService();

// Export types for use in other components
export type { ContactFormData, ContactStatus };
